import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { Jalali } from 'jalali-ts';
import { AlertifyService } from 'src/app/Service/Alertify.service';
import { ICService } from 'src/app/Service/IC/IC.service';

interface TrendSeries { key: string; title: string; unit: string; segments: string[]; points: { x: number; y: number; row: any }[]; }
@Component({ selector: 'app-ic-trend', templateUrl: './ICTrend.component.html', styleUrls: ['./ICTrend.component.css'] })
export class ICTrendComponent implements OnInit, OnDestroy {
  readonly labels = { loading: 'در حال دریافت نمودار...', retry: 'تلاش مجدد', empty: 'داده‌ای برای نمایش وجود ندارد.',
    noNumeric: 'مقدار عددی معتبر برای نمودار موجود نیست.', missing: 'ردیف با مقدار یا تاریخ ناموجود رسم نشده است.',
    truncated: 'فقط آخرین ۲۰۰۰ ردیف نمایش داده می‌شود.', date: 'تاریخ', value: 'مقدار نمودار / آماره', unit: 'واحد',
    result: 'نتیجه آیتم', status: 'وضعیت اجرا', data: 'وضعیت داده', unavailable: 'قابل نمایش نیست' };
  equipmentId = 0; itemId = 0; rows: any[] = []; loading = false; points = ''; error = '';
  series: TrendSeries[] = []; missingCount = 0;
  private request?: Subscription;
  constructor(private route: ActivatedRoute, private service: ICService, private alert: AlertifyService) {}
  ngOnInit(): void { this.equipmentId = Number(this.route.snapshot.paramMap.get('equipmentId')) || 0; this.itemId = Number(this.route.snapshot.paramMap.get('itemId')) || 0; if (this.equipmentId && this.itemId) this.load(); }
  ngOnDestroy(): void { this.request?.unsubscribe(); }
  load(): void {
    if (this.loading) return;
    this.loading = true; this.error = ''; this.rows = []; this.series = []; this.points = '';
    this.request = this.service.getTrend(this.equipmentId, this.itemId).pipe(finalize(() => this.loading = false)).subscribe({
      next: rows => { this.rows = rows || []; this.buildPoints(); },
      error: () => { this.error = 'دریافت نمودار ناموفق بود؛ دوباره تلاش کنید.'; this.alert.ErrorMessage(this.error); }
    });
  }
  numeric(row: any): number | null {
    if (row.valueState && row.valueState !== 'VALID') return null;
    const value = Object.prototype.hasOwnProperty.call(row, 'value') ? row.value : row.calculated_Value ?? row.measured_Value;
    if (value === null || value === undefined || !['number', 'string'].includes(typeof value) || typeof value === 'string' && !value.trim()) return null;
    const parsed = Number(value); return Number.isFinite(parsed) ? parsed : null;
  }
  dateValue(row: any): number | null { if (!row.date) return null; const time = Date.parse(row.date); return Number.isFinite(time) ? time : null; }
  dateTitle(row: any): string {
    if (this.dateValue(row) === null) return 'ثبت نشده';
    try { return Jalali.gregorian(row.date).format('YYYY/MM/DD'); } catch { return 'ثبت نشده'; }
  }
  display(value: unknown): string { return value === null || value === undefined || value === '' ? '—' : String(value); }
  buildPoints(): void {
    this.rows = [...this.rows].sort((a, b) => (this.dateValue(a) ?? Number.MAX_SAFE_INTEGER) - (this.dateValue(b) ?? Number.MAX_SAFE_INTEGER)
      || (a.executionId || 0) - (b.executionId || 0) || (a.resultId || 0) - (b.resultId || 0));
    this.missingCount = this.rows.filter(row => this.numeric(row) === null || this.dateValue(row) === null).length;
    const groups = new Map<string, any[]>();
    for (const row of this.rows) {
      const key = row.seriesKey || JSON.stringify([row.unit || null, row.statistic || 'Recorded value', row.templateVersion || null, row.calculationVersion || null]);
      if (!groups.has(key)) groups.set(key, []); groups.get(key)!.push(row);
    }
    this.series = [];
    for (const [key, rows] of groups) {
      const valid = rows.filter(r => this.numeric(r) !== null && this.dateValue(r) !== null); if (!valid.length) continue;
      const values = valid.map(r => this.numeric(r)!); const min = Math.min(...values), max = Math.max(...values), span = max - min || 1;
      const start = this.dateValue(valid[0])!, end = this.dateValue(valid[valid.length - 1])!;
      const series: TrendSeries = { key, title: `${rows[0].statistic || 'مقدار ثبت‌شده'} · نسخه ${rows[0].templateVersion ?? 'نامشخص'} / ${rows[0].calculationVersion || 'نامشخص'}`,
        unit: rows[0].unit || 'واحد ثبت نشده', segments: [], points: [] };
      let segment: string[] = [];
      for (const row of rows) {
        const value = this.numeric(row), time = this.dateValue(row);
        if (value === null || time === null) { if (segment.length) series.segments.push(segment.join(' ')); segment = []; continue; }
        const x = end === start ? 200 : 20 + 360 * (time - start) / (end - start), y = 180 - 150 * (value - min) / span;
        segment.push(`${x},${y}`); series.points.push({ x, y, row });
      }
      if (segment.length) series.segments.push(segment.join(' ')); this.series.push(series);
    }
    this.points = this.series[0]?.segments[0] || '';
  }
}
